import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * The button's visual variants, in their own file.
 *
 * Separate from `Button.tsx` for two reasons. An ANCHOR sometimes has to look like a button — a
 * download is navigation to a resource, so `<a href download>` is the correct element (ui-ux.md § 4)
 * while looking identical to the buttons beside it — and exporting a non-component from a component
 * file breaks Vite's fast refresh for that whole module.
 *
 * Shared rather than duplicated: a second set of classes that happen to match is how a design system
 * drifts.
 */
export const button = cva(
  cn(
    'inline-flex items-center justify-center gap-2 rounded-md font-medium',
    'transition-colors duration-150',
    // Never removed without a replacement — a keyboard user loses their place entirely.
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
    // Disabled is only ever used for "submission in progress", never to signal invalidity.
    'disabled:pointer-events-none disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-primary-fill text-primary-on-fill hover:bg-primary-fill-hover',
        secondary: 'bg-surface-2 text-foreground border border-border hover:bg-chip-hover',
        ghost: 'text-foreground hover:bg-chip',
        danger: 'bg-danger text-white hover:opacity-90',
        link: 'text-link underline underline-offset-2 hover:opacity-80',
      },
      size: {
        sm: 'h-8 px-2.5 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-11 px-5 text-sm',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);
